/**
 * Pure helper functions for biometric CSV import.
 * No side effects, no React, no API calls.
 */

export const OURA_SIGNATURE = ['Readiness Score', 'HRV Average'];
export const WHOOP_SIGNATURE = ['Recovery Score %', 'Day Strain'];

export type KnownVendor = 'oura' | 'whoop';

/**
 * Detect the wearable vendor from CSV column headers.
 * Returns null if the headers don't match any known vendor signature.
 */
export function detectVendor(headers: string[]): KnownVendor | null {
  const headerSet = new Set(headers);
  if (OURA_SIGNATURE.every((col) => headerSet.has(col))) return 'oura';
  if (WHOOP_SIGNATURE.every((col) => headerSet.has(col))) return 'whoop';
  return null;
}

/**
 * Returns the default CHAMBER field → CSV column name mapping for a vendor.
 * CHAMBER fields: date, hrv_ms, resting_hr, sleep_duration_min, sleep_score, readiness_score, strain
 */
export function getDefaultColumnMapping(vendor: KnownVendor | null): Record<string, string> {
  if (vendor === 'oura') {
    return {
      date: 'date',
      hrv_ms: 'HRV Average',
      resting_hr: 'Resting Heart Rate',
      sleep_duration_min: 'Total Sleep Duration',
      sleep_score: 'Sleep Score',
      readiness_score: 'Readiness Score',
      strain: '',
    };
  }
  if (vendor === 'whoop') {
    return {
      date: 'Cycle start time',
      hrv_ms: 'HRV',
      resting_hr: 'Resting Heart Rate',
      sleep_duration_min: 'Sleep Duration',
      sleep_score: 'Sleep Performance %',
      readiness_score: 'Recovery Score %',
      strain: 'Day Strain',
    };
  }
  return {
    date: '',
    hrv_ms: '',
    resting_hr: '',
    sleep_duration_min: '',
    sleep_score: '',
    readiness_score: '',
    strain: '',
  };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function parseNum(val: string | undefined): number | null {
  if (val === undefined || val === null || val.trim() === '') return null;
  const n = parseFloat(val.trim());
  return isNaN(n) ? null : n;
}

/**
 * Maps a single CSV row to a CHAMBER biometric record.
 * Returns null if the date field is missing or invalid.
 */
export function mapRowToRecord(
  row: Record<string, string>,
  mapping: Record<string, string>,
): Record<string, unknown> | null {
  const dateCol = mapping['date'];
  const rawDate = dateCol ? row[dateCol] : undefined;
  if (!rawDate) return null;

  // Accept YYYY-MM-DD prefix (Oura sometimes appends time)
  const match = rawDate.trim().match(DATE_RE);
  if (!match) return null;
  const date = match[0];

  const get = (field: string) => {
    const col = mapping[field];
    return col ? row[col] : undefined;
  };

  return {
    kind: 'daily',
    date,
    hrv_ms: parseNum(get('hrv_ms')),
    resting_hr: parseNum(get('resting_hr')),
    sleep_duration_min: parseNum(get('sleep_duration_min')),
    sleep_score: parseNum(get('sleep_score')),
    readiness_score: parseNum(get('readiness_score')),
    strain: parseNum(get('strain')),
  };
}
